import React, { useEffect, useMemo, useState } from "react";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import { useTranslation } from "react-i18next";
import Input from "components/Input";
import useStyles from "./styles";
import customTheme from "config/theme";
import {
  getStaticCountries,
  getStaticStates,
  getStaticCitiesOfState,
} from "api/cities";

type Option = { label: string; value: string };

interface LocationModalProps {
  open: boolean;
  onClose: () => void;
  onSaveLocation: (locationText: string) => void;
}

const LocationModal = React.memo((props: LocationModalProps) => {
  const { open, onClose, onSaveLocation } = props;
  const classes = useStyles();
  const translate = useTranslation().t;

  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [countries, setCountries] = useState<Option[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<Option | null>(null);

  const [states, setStates] = useState<Option[]>([]);
  const [selectedState, setSelectedState] = useState<Option | null>(null);

  const [cities, setCities] = useState<Option[]>([]);
  const [selectedCity, setSelectedCity] = useState<Option | null>(null);

  const safeReset = () => {
    setCountries([]);
    setSelectedCountry(null);
    setStates([]);
    setSelectedState(null);
    setCities([]);
    setSelectedCity(null);
  };

  const fetchCountries = async (): Promise<Option[]> => {
    setIsLocationLoading(true);
    try {
      const list = await getStaticCountries();
      const formattedList = list.map((country) => ({
        label: country.name,
        value: country.isoCode,
      }));
      setCountries(formattedList);
      return formattedList;
    } finally {
      setIsLocationLoading(false);
    }
  };

  const fetchStates = async (countryValue?: string): Promise<Option[] | null> => {
    const country = countryValue || (selectedCountry ? selectedCountry.value : null);
    if (!country) return null;
    setIsLocationLoading(true);
    try {
      const list = await getStaticStates(country);
      if (!list) return null;
      const formattedList = list.map((state) => ({
        label: state.name,
        value: state.isoCode,
      }));
      setStates(formattedList);
      return formattedList;
    } finally {
      setIsLocationLoading(false);
    }
  };

  const fetchCitiesOfState = async (
    countryValue?: string,
    stateValue?: string
  ): Promise<Option[] | null> => {
    const country = countryValue || (selectedCountry ? selectedCountry.value : null);
    const state = stateValue || (selectedState ? selectedState.value : null);
    if (!country || !state) return null;
    setIsLocationLoading(true);
    try {
      const list = await getStaticCitiesOfState(country, state);
      if (!list) return null;
      const formattedList = list.map((city) => ({
        label: city.name,
        value: city.name,
      }));
      setCities(formattedList);
      return formattedList;
    } finally {
      setIsLocationLoading(false);
    }
  };

  const handleCountryChange = (selectedOption: any) => {
    setSelectedCountry(selectedOption);
    setSelectedState(null);
    setSelectedCity(null);
    setStates([]);
    setCities([]);
    if (selectedOption && selectedOption.value) {
      fetchStates(selectedOption.value);
    }
  };

  const handleStateChange = (selectedOption: any) => {
    setSelectedState(selectedOption);
    setSelectedCity(null);
    setCities([]);
    if (selectedOption && selectedOption.value && selectedCountry) {
      fetchCitiesOfState(selectedCountry.value, selectedOption.value);
    }
  };

  const handleSaveLocation = () => {
    if (!selectedCountry || !selectedState) return;
    const parts = selectedCity
      ? [selectedCity, selectedState, selectedCountry]
      : [selectedState, selectedCountry];

    const locationText = parts.map((p) => p.label).join(", ");
    onSaveLocation(locationText);
    onClose();
  };

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const init = async () => {
      safeReset();
      await fetchCountries();
      if (cancelled) return;
    };

    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const modalOpen = useMemo(() => open, [open]);

  return (
    <Modal
      className={classes.modal}
      open={modalOpen}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={modalOpen}>
        <div className={classes.locationModalPaper}>
          <div className={classes.locationModalTitleContainer}>
            <h2 className={classes.modalTitle}>{translate("EDIT_LOCATION")}</h2>
            {isLocationLoading && (
              <CircularProgress
                size={20}
                style={{
                  color: (customTheme as any).bgButton,
                  marginLeft: 15,
                }}
              />
            )}
          </div>

          <Input
            name="modalCountry"
            type="select2"
            fullWidth
            label={translate("COUNTRY")}
            placeholder={translate("SELECT_COUNTRY") || "Select Country"}
            options={countries}
            value={selectedCountry}
            changeHandler={handleCountryChange}
            required
          />

          <Input
            name="modalState"
            type="select2"
            fullWidth
            label={translate("STATE")}
            placeholder={translate("SELECT_STATE") || "Select State"}
            options={states}
            value={selectedState}
            changeHandler={handleStateChange}
            disabled={!selectedCountry || isLocationLoading}
            required
          />

          <Input
            name="modalCity"
            type="select2"
            fullWidth
            label={translate("CITY") + " (Optional)"}
            placeholder={translate("SELECT_CITY") || "Select City (Optional)"}
            options={cities}
            value={selectedCity}
            changeHandler={(selectedOption: any) => setSelectedCity(selectedOption)}
            disabled={!selectedState || isLocationLoading}
          />

          <div className={classes.modalActions}>
            <Button onClick={onClose} variant="outlined">
              {translate("CANCEL")}
            </Button>
            <Button
              onClick={handleSaveLocation}
              variant="contained"
              className={classes.saveButton}
              disabled={!selectedCountry || !selectedState || isLocationLoading}
            >
              {translate("SAVE")}
            </Button>
          </div>
        </div>
      </Fade>
    </Modal>
  );
});

export default LocationModal;

